/**
 * Luxifer 6 — First-party analytics client.
 *
 * - Persistent session ID (30 min inactivity window; then a new session)
 * - Batches events client-side; flushes every 3s and on pagehide/visibilitychange
 * - Captures UTM parameters from URL and persists them for the session
 * - Auto-tracks: page views, scroll depth (25/50/75/100), file downloads, external clicks
 * - Manual API: track(event, {...})
 *
 * Zero deps. Safe for SSR. Never blocks the UI thread.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "lx6_session";
const SESSION_TTL_MIN = 30;
const UTM_KEY = "lx6_utm";
const SCROLL_KEY = "lx6_scroll_hits";

const now = () => Date.now();

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getSession() {
  if (typeof window === "undefined") return "server";
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s.last && now() - s.last < SESSION_TTL_MIN * 60_000) {
        s.last = now();
        localStorage.setItem(SESSION_KEY, JSON.stringify(s));
        return s.id;
      }
    }
  } catch {}
  const s = { id: uuid(), last: now() };
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch {}
  return s.id;
}

function readUTMs() {
  if (typeof window === "undefined") return {};
  try {
    const p = new URLSearchParams(window.location.search);
    const utm = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
      const v = p.get(k);
      if (v) utm[k] = v;
    });
    if (Object.keys(utm).length > 0) {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
      return utm;
    }
    const stored = sessionStorage.getItem(UTM_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch { return {}; }
}

// ── Batching ──
const queue = [];
let flushTimer = null;

function flush(useBeacon = false) {
  if (!queue.length) return;
  const events = queue.splice(0, queue.length);
  const payload = JSON.stringify({ events });
  try {
    if (useBeacon && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(`${API}/track`, new Blob([payload], { type: "application/json" }));
      if (ok) return;
    }
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {
      // Silently swallow — analytics must never break the app
    });
  } catch {
    // ignore
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => { flushTimer = null; flush(false); }, 3000);
}

export function track(eventName, extra = {}) {
  if (typeof window === "undefined") return;
  try {
    const utm = readUTMs();
    const evt = {
      event: eventName,
      session_id: getSession(),
      path: window.location.pathname + window.location.search,
      referrer: extra.referrer !== undefined ? extra.referrer : document.referrer || "",
      ...utm,
      ts: new Date().toISOString(),
      ...extra,
    };
    queue.push(evt);
    scheduleFlush();
  } catch { /* never block UI */ }
}

// ── Auto-track scroll depth (one hit per depth per page load) ──
function initScrollDepth() {
  if (typeof window === "undefined") return;
  const hits = new Set();
  const check = () => {
    const doc = document.documentElement;
    const scrollTop = doc.scrollTop || document.body.scrollTop;
    const winH = window.innerHeight;
    const docH = doc.scrollHeight;
    const pct = Math.min(100, Math.round(((scrollTop + winH) / Math.max(1, docH)) * 100));
    [25, 50, 75, 100].forEach((mark) => {
      if (pct >= mark && !hits.has(mark)) {
        hits.add(mark);
        track("scroll_depth", { target_label: `${mark}`, meta: { pct: mark } });
      }
    });
  };
  const onScroll = () => { requestAnimationFrame(check); };
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => window.removeEventListener("scroll", onScroll);
}

// ── Auto-track downloads + outbound links ──
function initClickAndDownload() {
  if (typeof window === "undefined") return;
  const handler = (e) => {
    const a = e.target && e.target.closest && e.target.closest("a[href]");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
    const isDownload = /\.(mp3|wav|zip|pdf|png|jpg|jpeg|gif|mp4|mov)(\?|$)/i.test(href) || a.hasAttribute("download");
    let isOutbound = false;
    try {
      const u = new URL(href, window.location.href);
      if (u.host && u.host !== window.location.host) isOutbound = true;
    } catch {}
    if (isDownload) {
      track("download_started", { target_id: href, target_label: a.textContent.trim().slice(0, 100) });
    } else if (isOutbound) {
      track("outbound_click", { target_id: href, target_label: a.textContent.trim().slice(0, 100), meta: { href } });
    }
  };
  document.addEventListener("click", handler, { capture: true });
}

// ── Page views on SPA route changes ──
let lastPath = "";
export function trackPageView(pathname) {
  const path = pathname || (typeof window !== "undefined" ? window.location.pathname : "");
  if (path === lastPath) return;
  lastPath = path;
  track("page_view", { referrer: document.referrer || "" });
}

// ── Init once ──
let initialized = false;
export function initAnalytics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  readUTMs(); // persist any UTMs on first load
  initScrollDepth();
  initClickAndDownload();
  trackPageView();
  // Flush on tab hide / navigate away — using sendBeacon to survive
  const hideHandler = () => flush(true);
  window.addEventListener("pagehide", hideHandler);
  window.addEventListener("beforeunload", hideHandler);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
}

export default { initAnalytics, track, trackPageView };
